import GoogleMapReact from 'google-map-react';
import { observer } from 'mobx-react';
import { FC, useCallback, useMemo } from 'react';
import { useCheckoutStoreContext } from '../../context';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { defaultBranch } from 'src/constants/branches';
import { DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import { appStore } from 'src/mobx/appStore';
import { ICheckoutDependencies } from 'src/mobx/checkoutStore/types';
import { modalStore } from 'src/mobx/modalStore';
import { userStore } from 'src/mobx/userStore';
import { IBranch } from 'src/types/branches';
import { getTranslate } from 'src/utils/helpers';
import Icon from 'src/view/components/Controls/Icon/Icon';
import PinIndicator from 'src/view/components/Controls/PinIndicator/PinIndicator';
import './style.scss';

const OrderMiniMap: FC = observer((): JSX.Element => {
  const { store: checkoutStore } = useCheckoutStoreContext();

  const onAddressChange = useCallback(
    (branch: IBranch | undefined, location: ICheckoutDependencies['location'], delivery_type: DELIVERY_TYPE) => {
      checkoutStore.selectBranch(branch || defaultBranch);
      checkoutStore.selectLocation(location);

      if (delivery_type === DELIVERY_TYPE.DINE_IN) {
        checkoutStore.changeDeliveryType(DELIVERY_TYPE.PICKUP);
        checkoutStore.toggleDineIn(1);
        checkoutStore.toggleCurbside(0);
        checkoutStore.defineDinein(checkoutStore.dependencies.branch);
      } else {
        checkoutStore.changeDeliveryType(delivery_type);
        checkoutStore.toggleDineIn(0);
      }
    },
    [checkoutStore.dependencies.branch]
  );

  const openBranchPicker = useCallback(() => {
    modalStore.openDeliveryType({
      editable: checkoutStore.isDraft,
      location: checkoutStore.dependencies.location,
      customer: checkoutStore.dependencies.customer?.id,
      branch: checkoutStore.dependencies.branch,
      delivery_type: checkoutStore.params.is_dinein
        ? DELIVERY_TYPE.DINE_IN
        : checkoutStore.params.delivery_type === 'p'
        ? DELIVERY_TYPE.PICKUP
        : DELIVERY_TYPE.DELIVERY,
      onChange: onAddressChange,
    });
  }, [
    checkoutStore.isDraft,
    checkoutStore.dependencies.location,
    checkoutStore.dependencies.customer,
    checkoutStore.dependencies.branch,
    checkoutStore.params.is_dinein,
    checkoutStore.params.delivery_type,
  ]);

  const branchNameClassName = useMemo((): string => {
    let className: string = 'family-bold text-size-18 text-elipsis';

    // if a branch inaccessible to user is selected
    const isSelectUnavailableBranch: boolean =
      !userStore.user.branches?.length || !userStore.user.branches.some((item) => item.id === checkoutStore.dependencies.branch.id);
    if (isSelectUnavailableBranch) className += ' color-gray';

    return className;
  }, [userStore.user.branches, checkoutStore.dependencies.branch]);

  const center = useMemo(() => {
    const { branch, location } = checkoutStore.dependencies;

    return checkoutStore.params.delivery_type === DELIVERY_TYPE.PICKUP
      ? { lat: branch.latitude, lng: branch.longitude }
      : { lat: location?.lat || 0, lng: location?.lng || 0 };
  }, [checkoutStore.dependencies.branch, checkoutStore.dependencies.location, checkoutStore.params.delivery_type]);

  return (
    <div onClick={openBranchPicker} className="order-minimap">
      {/* map */}
      <div className="d-flex order-minimap-component">
        <GoogleMapReact
          zoom={15}
          bootstrapURLKeys={{
            key: appStore.settings?.google_maps_pk || '',
            libraries: ['geometry'],
          }}
          center={center}
          defaultZoom={15}
          yesIWantToUseGoogleMapApiInternals
          options={{
            fullscreenControl: false,
            gestureHandling: 'greedy',
            zoomControl: false,
            scrollwheel: true,
            draggable: false,
          }}
        >
          <PinIndicator name={Icons.MapLocation} lat={center.lat} lng={center.lng} className="color-mainblue" />
        </GoogleMapReact>
      </div>

      <div className="d-flex flex-column mt-4">
        {/* branch name */}
        {checkoutStore.dependencies.branch.id ? (
          <span className={branchNameClassName}>{getTranslate(checkoutStore.dependencies.branch.name)}</span>
        ) : null}
        {/* address */}
        {checkoutStore.dependencies.location?.address && checkoutStore.params.delivery_type === DELIVERY_TYPE.DELIVERY ? (
          <div className="d-flex align-items-center mt-2 flex-row color-gray2">
            <Icon name={Icons.Pin} />
            <span className="text-size-14 ml-1">{checkoutStore.dependencies.location.address}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default OrderMiniMap;
